import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons/faClockRotateLeft";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tab, TextField, Typography } from '@mui/material';
import { APP_PAGE_SIZE_MEDIUM } from "app/appConstants";
import { MprcCard, MprcIconButton, MprcIconButtonDropdown, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from 'components';
import { MprcFilterLine } from 'components/filter';
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconSort } from "components/icons";
import { MprcChipTabs } from "components/MprcChipTabs";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { AppointmentDayDto, AppointmentDto, AppointmentDtoPaginatedList, CalendarAppointmentsOrderBy, CalendarFilterBy, MeetingType } from "generated/CoreApi_v1";
import { useSimpleDebounce } from "hooks/useDebounce";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import moment from "moment";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { InstructorEditMeeting } from "views/lms/meeting/edit/InstructorEditMeeting";
import { MeetingViewer } from "views/lms/meeting/views/MeetingViewer";
import { AppointmentBaseFilter, AppointmentBaseOption, AppointmentsCalendarList } from "../../instructor/components/AppointmentsCalendarList";

////////////////////////////////////////////////////////////////////////////

interface StudentMeetingsFilter extends AppointmentBaseFilter {
    searchText: string;
    includePast: boolean;
    orderBy: CalendarAppointmentsOrderBy;
}

type StudentMeetingOption = AppointmentBaseOption | "editMeeting" | "attendanceList" | "trainingPreview" | "meetingPreview" | "publishRecording" | "assignAttendance" | "trainingView";

interface StudentMeetingsProps {
    filterBy?: CalendarFilterBy;
}

type StateData = {
    filter: StudentMeetingsFilter;
    filterBy: CalendarFilterBy;
    pageNumber: number;
    isInstructor: boolean;
};

const initialState = (filterDay: string | null, includePast: boolean = false) => {
    return {
        includePast: includePast,
        orderBy: CalendarAppointmentsOrderBy.MeetingDate,
        searchText: "",
        meetingDate: filterDay != null ? new Date(decodeURIComponent(filterDay)).convertLocalToUtcDate() : null,
        onlySpecificDay: filterDay != null,
    } as StudentMeetingsFilter;
};

////////////////////////////////////////////////////////////////////////////

export const StudentMeetings: React.FC<StudentMeetingsProps> = (props) => {
    const { t } = useTranslation("lms");
    const { user: currentUser } = useSession();

    const api = useCoreApi();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams, setSearchParams] = useSearchParams();
    const paramDt = searchParams.get("dt");

    const [viewData, setViewData] = useState<AppointmentDtoPaginatedList | null>(null);
    const [appointmentDays, setAppointmentDays] = useState<AppointmentDayDto[]>([]);
    const [dialog, setDialog] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    ////////////////////////////////////////////////////////////////////////////

    const stateData = useMemo(() => {
        return location.state == undefined
            ? {
                filter: initialState(paramDt, false),
                filterBy: props.filterBy ?? CalendarFilterBy.All,
                pageNumber: 1,
                isInstructor: false
            } as StateData
            : location.state as StateData;
    }, [location.state, paramDt]);

    const pageNumber = stateData.pageNumber;
    const filter = stateData.filter;
    const filterBy = stateData.filterBy;
    const isInstructor = stateData.isInstructor;

    const [searchText, setSeachText] = useState<string>(stateData.filter.searchText);
    const filterSearchText = useSimpleDebounce(searchText, 500);

    ////////////////////////////////////////////////////////////////////////////


    ///
    const loadData = () => {
        setLoading(true);

        api.v1.lmsStudent.getCalendarAppointments({
            searchText: filter.searchText ?? null,
            includePast: filter.includePast,
            filterBy: filterBy,
            orderBy: filter.orderBy,
            meetingDate: filter.meetingDate,
            onlySpecificDay: filter.onlySpecificDay,
            pageNumber,
            pageSize: APP_PAGE_SIZE_MEDIUM
        })
            .then(({ data }) => setViewData(data))
            .catch(e => {
                setViewData(null);
                setViewError(e);
            })
            .finally(() => setLoading(false));
    };

    ///
    const loadCalendar = () => {
        api.v1.lmsStudent.getCalendar({
            includePast: filter.includePast,
            meetingDate: filter.meetingDate ?? new Date(),
            filterBy: filterBy,
            browerTimeZoneId: moment.tz.guess()
        })
            .then(({ data }) => setAppointmentDays(data))
            .catch(e => setViewError(e));
    };

    ///
    const handleFilter = (data: Partial<StudentMeetingsFilter>) => {
        navigate(document.location.search, { replace: true, state: { ...stateData, pageNumber: 1, filter: { ...filter, ...data } } });
    };

    ///
    const changeState = (data: Partial<StateData>) => {
        navigate(document.location.search, { replace: true, state: { ...stateData, ...data } });
    };

    ///
    const handleResetFilters = () => {
        setSeachText("");
        handleFilter({ searchText: "" });
    };

    ///
    const getOptions = useCallback(
        (item: AppointmentDto): MprcDropdownItems<StudentMeetingOption> => {

            const group1: MprcDropdownItems<StudentMeetingOption> = [];
            const group2: MprcDropdownItems<StudentMeetingOption> = [];
            const group3: MprcDropdownItems<StudentMeetingOption> = [];

            if (item.meeting) {
                if (item.meeting.isMeetingInstructor) {
                    if (item.meeting.canEditMeeting)
                        group1.push({ text: t("Student.Meetings.EditMeetingAction"), value: "editMeeting" });

                    group1.push({ text: t("Student.Meetings.AttendanceListAction"), value: "attendanceList" });

                    group2.push({ text: t("Student.Meetings.TrainingPreviewAction"), value: "trainingPreview" });
                    group2.push({ text: t("Student.Meetings.MeetingPreviewAction"), value: "meetingPreview" });

                    if (item.finished) {
                        group3.push({ text: t("Student.Meetings.PublishRecordingAction"), value: "publishRecording" });
                        group3.push({ text: t("Student.Meetings.AssignAttendanceAction"), value: "assignAttendance" });
                    }
                }
                else {
                    group1.push({ text: t("Student.Meetings.TrainingViewAction"), value: "trainingView" });
                    group1.push({ text: t("Student.Meetings.MeetingPreviewAction"), value: "meetingPreview" });
                }
            }
            else {
                group1.push({ text: t("Instructor.List.Meetings.EditAppointmentAction"), value: "editAppointment" });
                group1.push({ text: <MprcText color="error">{t("Instructor.List.Meetings.DeleteAppointmentAction")}</MprcText>, value: "deleteAppointment" });
            }

            return MprcDropdownBuildGroupedItems<StudentMeetingOption>([group1, group2, group3]);
        },
        [t]
    );

    ///
    const handleSelectOption = (option: StudentMeetingOption, item: AppointmentDto) => {
        switch (option) {
            case "editMeeting":
                setDialog(
                    <InstructorEditMeeting
                        classroomId={item.meeting!.classroomId}
                        componentId={item.meeting!.componentId}
                        meetingName={item.subject}
                        openTo={"location"}
                        onClose={(success) => { setDialog(null); }}
                    />
                );
                break;
            case "trainingPreview":
                navigate(LmsPaths.BuildEnrollmentPreview(item.meeting!.trainingId, item.meeting!.classroomId));
                break;
            case "trainingView":
                navigate(LmsPaths.BuildEnrollmentViewer(item.meeting!.trainingType, item.meeting!.enrollmentId!));
                break;
            case "meetingPreview":
                setDialog(
                    <MeetingViewer
                        open
                        componentId={item.meeting!.componentId}
                        meetingType={item.meeting!.meetingType}
                        classroomId={item.meeting!.classroomId}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
            case "publishRecording":
                setDialog(
                    <InstructorEditMeeting
                        classroomId={item.meeting!.classroomId}
                        componentId={item.meeting!.componentId}
                        meetingName={item.subject}
                        openTo={"recording"}
                        onClose={(success) => { setDialog(null); }}
                    />
                );
                break;
            case "assignAttendance":
                navigate(LmsPaths.BuildSupervisorRegisterMeeting(item.meeting!.classroomId, item.meeting!.componentId));
                break;
            case "attendanceList":
                setDialog(
                    <InstructorEditMeeting
                        classroomId={item.meeting!.classroomId}
                        componentId={item.meeting!.componentId}
                        meetingName={item.subject}
                        openTo={"attendance"}
                        onClose={(success) => { setDialog(null); }}
                    />
                );
                break;
        }
    };

    ///
    const handleFilterBy = (filterBy: CalendarFilterBy, isInstructor: boolean) => {
        const state = { filter: initialState(null, stateData.filter.includePast), isInstructor, filterBy, pageNumber: 1 } as StateData;
        switch (filterBy) {
            case CalendarFilterBy.All:
                navigate(LmsPaths.AllMeetings, { replace: true, state });
                break;
            case CalendarFilterBy.Instructor:
                navigate(LmsPaths.InstructorMeetings, { replace: true, state });
                break;
            case CalendarFilterBy.Participant:
                navigate(LmsPaths.StudentMeetings, { replace: true, state });
                break;
        }
    };

    useEffect(() => {
        api.v1.lmsStudent.isMeetingInstructor({ includePast: filter.includePast })
            .then(({ data }) => {
                changeState({ isInstructor: data });

                if (isInstructor != data && filterBy != CalendarFilterBy.All) {
                    handleFilterBy(CalendarFilterBy.All, data);
                }
            })
            .catch(e => console.error(e));
    }, [stateData.filter.includePast]);

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        loadCalendar();
    }, [stateData.filter.includePast, stateData.filter.meetingDate, stateData.filterBy]);

    useEffect(() => {
        loadData();
    }, [stateData.filter, stateData.pageNumber, stateData.filterBy]);

    useDidUpdateEffect(() => {
        handleFilter({ searchText: filterSearchText });
    }, [filterSearchText]);

    useEffect(() => {
        if (props.filterBy != filterBy && props.filterBy != null) changeState({ filterBy: props.filterBy });
    }, [props.filterBy]);

    useEffect(() => {
        const dlg = searchParams.get("dlg") as StudentMeetingOption;
        if (dlg != null) {
            const componentId = searchParams.get("comp");
            const classroomId = searchParams.get("clr");
            const meetingType: MeetingType = MeetingType[searchParams.get("mt") as any] as any;

            switch (dlg) {
                case "meetingPreview":
                    if (componentId && classroomId && meetingType)
                        setDialog(
                            <MeetingViewer
                                open
                                componentId={componentId}
                                meetingType={meetingType}
                                classroomId={classroomId}
                                onClose={() => setDialog(null)}
                            />
                        );
                    break;
                default: setDialog(null);
            }
            searchParams.delete("dlg");
            searchParams.delete("comp");
            searchParams.delete("clr");
            searchParams.delete("mt");
            setSearchParams(searchParams);
        }
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    if (viewError)
        return <MprcRequestErrorReport error={viewError} />;

    return (
        <>
            {dialog}

            <MprcRequestIndicator visible={loading} type={loading ? "request" : "post"} />

            <MprcCard>
                <MprcCardFilterBox>
                    <MprcFilterLine>
                        <Typography variant="h3" noWrap component="div">
                            {t("Student.Meetings.Title")}
                        </Typography>
                        {isInstructor === true &&
                            <MprcChipTabs
                                value={filterBy}
                                onChange={(e, value) => handleFilterBy(value, isInstructor)}
                            >
                                <Tab
                                    label={t("Student.Meetings.AllOption")}
                                    value={CalendarFilterBy.All}
                                />
                                <Tab
                                    label={t("Student.Meetings.InstructorOption")}
                                    value={CalendarFilterBy.Instructor}
                                />
                                <Tab
                                    label={t("Student.Meetings.ParticipantOption")}
                                    value={CalendarFilterBy.Participant}
                                />
                            </MprcChipTabs>
                        }
                        <div>
                            <MprcIconButton
                                title={t("Student.Meetings.IncludePastButtonTooltip")}
                                color={filter.includePast ? "highlight" : "inherit"}
                                onClick={() => handleFilter({ includePast: !filter.includePast })}
                            >
                                <FontAwesomeIcon icon={faClockRotateLeft} />
                            </MprcIconButton>
                            <MprcIconButtonDropdown
                                menuItemCheck
                                icon={<IconSort />}
                                selected={filter.orderBy}
                                onChange={(orderBy) => handleFilter({ orderBy: orderBy })}
                                items={[
                                    { text: t("Student.Meetings.OrderBy.DateText"), value: CalendarAppointmentsOrderBy.MeetingDate },
                                    { text: t("Student.Meetings.OrderBy.NameText"), value: CalendarAppointmentsOrderBy.MeetingName },
                                    { text: t("Student.Meetings.OrderBy.TrainingNameText"), value: CalendarAppointmentsOrderBy.TrainingName },
                                ]}
                                title={t("Student.Meetings.OrderByButtonTooltip")}
                            />
                        </div>
                    </MprcFilterLine>
                    <TextField
                        fullWidth
                        placeholder={t("Student.Meetings.SearchTextPlaceholder")}
                        autoFocus
                        autoComplete="off"
                        value={searchText}
                        onChange={(e) => setSeachText(e.currentTarget.value)}
                    />
                </MprcCardFilterBox>
                {viewData != null &&
                    <AppointmentsCalendarList
                        instructorId={currentUser.userId}
                        appointmentDays={appointmentDays}
                        data={viewData}
                        filter={filter}
                        isFiltered={(filter.searchText?.length || 0) > 0}
                        isLoading={loading}
                        canCreateBlockAppointments={isInstructor === true && filter.includePast === false && filterBy != CalendarFilterBy.Participant}
                        onGetMenuOptions={getOptions}
                        onSelectMenuOption={handleSelectOption}
                        onChangePageNumber={(pgNumber: number) => {
                            if (pgNumber != pageNumber) changeState({ pageNumber: pgNumber });
                        }}
                        onFilter={data => handleFilter(data)}
                        onReload={() => {
                            loadCalendar();
                            loadData();
                        }}
                        onResetFilters={handleResetFilters}
                    />}
            </MprcCard >
        </>
    );
};
